/** Protocol v1 between a realtime client and a room (docs/use-cases/realtime.spec.md, Protocol v1). */
export const PROTOCOL_VERSION = 1

/** Largest accepted client frame, in UTF-8 bytes. */
export const MAX_MESSAGE_BYTES = 2048

/** Complete encoded edit frame limit. */
export const MAX_EDIT_MESSAGE_BYTES = 256 * 1024

/** Dropped messages per connection after which the room closes it with {@link POLICY_VIOLATION}. */
export const MAX_INVALID_MESSAGES = 3

/** WebSocket close code 1008 "policy violation" (RFC 6455 section 7.4.1). */
export const POLICY_VIOLATION = 1008

/** WebSocket close code 1006 "abnormal closure": the connection was lost without a close frame. */
export const ABNORMAL_CLOSURE = 1006

/** Who a connection belongs to; set by the server's auth, never by the client. */
export interface Identity {
  userId: string
  name: string
  avatar: string | null
}

export interface PresenceState {
  /** `null`: on no page. */
  pageId: string | null
  /** Opaque selection anchors; the room only size-caps it. */
  sel: unknown
  active: boolean
}

export interface Peer {
  connId: string
  identity: Identity
  state: PresenceState
}

export interface LiveEdit {
  pageId: string
  update: string
}

export type ClientMessage =
  | ({ v: typeof PROTOCOL_VERSION; t: 'edit' } & LiveEdit)
  | { v: typeof PROTOCOL_VERSION; t: 'state'; state: PresenceState }
  | { v: typeof PROTOCOL_VERSION; t: 'poke' }

export type ServerMessage =
  | ({ v: typeof PROTOCOL_VERSION; t: 'edit'; connId: string; userId: string } & LiveEdit)
  | { v: typeof PROTOCOL_VERSION; t: 'welcome'; connId: string; identity: Identity; edits?: true }
  | { v: typeof PROTOCOL_VERSION; t: 'snapshot'; pageId: string; peers: Peer[] }
  | { v: typeof PROTOCOL_VERSION; t: 'state'; peer: Peer }
  | { v: typeof PROTOCOL_VERSION; t: 'leave'; connId: string }
  | { v: typeof PROTOCOL_VERSION; t: 'poke' }

const encoder = new TextEncoder()

/** Parses one client frame; `undefined` for anything the room must drop. Unknown fields are discarded. */
export function parseClientMessage(raw: unknown): ClientMessage | undefined {
  if (typeof raw !== 'string') return undefined
  // A UTF-8 char takes at most 3 bytes per UTF-16 unit, so short strings skip the encode.
  if (raw.length * 3 > MAX_EDIT_MESSAGE_BYTES && encoder.encode(raw).byteLength > MAX_EDIT_MESSAGE_BYTES) return undefined

  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return undefined
  }
  if (!isRecord(value) || value.v !== PROTOCOL_VERSION) return undefined

  if (value.t === 'edit') {
    if (typeof value.pageId !== 'string' || value.pageId.length === 0) return undefined
    if (typeof value.update !== 'string' || value.update.length === 0) return undefined
    return { v: PROTOCOL_VERSION, t: 'edit', pageId: value.pageId, update: value.update }
  }
  if (raw.length * 3 > MAX_MESSAGE_BYTES && encoder.encode(raw).byteLength > MAX_MESSAGE_BYTES) return undefined

  if (value.t === 'poke') return { v: PROTOCOL_VERSION, t: 'poke' }
  if (value.t !== 'state' || !isRecord(value.state)) return undefined

  const { pageId, sel, active } = value.state
  if (pageId !== null && typeof pageId !== 'string') return undefined
  if (typeof active !== 'boolean') return undefined
  return { v: PROTOCOL_VERSION, t: 'state', state: { pageId, sel: sel ?? null, active } }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
